import React, { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { submitToHubSpot } from "../../services/hubspot";

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

interface FormStatus {
  type: "idle" | "loading" | "success" | "error";
  message?: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [status, setStatus] = useState<FormStatus>({ type: "idle" });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: "loading" });

    try {
      await submitToHubSpot(formData);

      setStatus({
        type: "success",
        message: "Thank you! Your message has been sent successfully.",
      });
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      setStatus({
        type: "error",
        message:
          "Failed to send message. Please try again or email us directly.",
      });
    }
  };

  return (
    <div className="bg-dark-bg min-h-screen">
      <div className="container mx-auto w-full max-w-4xl px-5 md:px-0 text-center">
        <h1 className="text-5xl md:text-[3.5rem] leading-none font-semibold pt-[10%]">
          <span className="bg-gradient-text bg-clip-text text-transparent">
            Get in Touch
          </span>
        </h1>

        <p className="mt-6 text-white text-lg max-w-3xl mx-auto leading-relaxed">
          Have questions about Data Revival? Want to see how we can help unlock
          the potential in your chemical archives? We'd love to hear from you.
        </p>
      </div>

      {/* Contact info cards */}
      <section className="mt-16 pb-16">
        <div className="container mx-auto w-full max-w-4xl px-5 md:px-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-white text-center transition-all duration-300 hover:border-white/30 hover:shadow-lg hover:shadow-[#00A3FF]/10">
              <div className="mb-4 inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-[#00A3FF] to-[#0077CC] shadow-lg shadow-[#00A3FF]/20">
                <Mail size={24} className="text-white" />
              </div>
              <h5 className="text-lg font-bold mb-2">Email</h5>
              <p className="text-gray-300 text-sm">
                <a
                  href="mailto:contact@data-revival.com"
                  className="hover:text-[#00A3FF] transition-colors"
                >
                  contact@data-revival.com
                </a>
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-white text-center transition-all duration-300 hover:border-white/30 hover:shadow-lg hover:shadow-[#00A3FF]/10">
              <div className="mb-4 inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-[#00A3FF] to-[#0077CC] shadow-lg shadow-[#00A3FF]/20">
                <Phone size={24} className="text-white" />
              </div>
              <h5 className="text-lg font-bold mb-2">Phone</h5>
              <p className="text-gray-300 text-sm">+44 (0) 123 456 7890</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-white text-center transition-all duration-300 hover:border-white/30 hover:shadow-lg hover:shadow-[#00A3FF]/10">
              <div className="mb-4 inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-[#00A3FF] to-[#0077CC] shadow-lg shadow-[#00A3FF]/20">
                <MapPin size={24} className="text-white" />
              </div>
              <h5 className="text-lg font-bold mb-2">Location</h5>
              <p className="text-gray-300 text-sm">
                Southampton, United Kingdom
              </p>
            </div>
          </div>

          {/* form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-10 transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-left">
              Send us a Message
            </h2>

            {status.type === "success" && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/50 rounded-lg text-green-400">
                {status.message}
              </div>
            )}

            {status.type === "error" && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400">
                {status.message}
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-white font-medium mb-2"
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    disabled={status.type === "loading"}
                    className="w-full px-4 py-3 bg-dark-bg/50 text-white border border-white/10 rounded-lg focus:outline-none focus:border-[#00A3FF] focus:ring-1 focus:ring-[#00A3FF]/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-white font-medium mb-2"
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    disabled={status.type === "loading"}
                    className="w-full px-4 py-3 bg-dark-bg/50 text-white border border-white/10 rounded-lg focus:outline-none focus:border-[#00A3FF] focus:ring-1 focus:ring-[#00A3FF]/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-white font-medium mb-2"
                >
                  Company / Organisation
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  disabled={status.type === "loading"}
                  className="w-full px-4 py-3 bg-dark-bg/50 text-white border border-white/10 rounded-lg focus:outline-none focus:border-[#00A3FF] focus:ring-1 focus:ring-[#00A3FF]/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Your company"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-white font-medium mb-2"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  disabled={status.type === "loading"}
                  className="w-full px-4 py-3 bg-dark-bg/50 text-white border border-white/10 rounded-lg focus:outline-none focus:border-[#00A3FF] focus:ring-1 focus:ring-[#00A3FF]/50 transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Tell us about your project or ask us a question..."
                />
              </div>

              <div className="text-left">
                <button
                  type="submit"
                  disabled={status.type === "loading"}
                  className="inline-flex items-center justify-center bg-gradient-to-r from-[#00A3FF] to-[#0077CC] hover:shadow-lg hover:shadow-[#00A3FF]/30 rounded-lg px-8 py-3 text-white font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status.type === "loading" ? (
                    <>
                      Sending...{" "}
                      <Send className="ml-2 animate-pulse" size={18} />
                    </>
                  ) : (
                    <>Send message</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
